import { useMemo, useCallback } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';

export const itineraryPages = {
  destination: { key: 'destination', path: '', name: 'Destination' },
  program: { key: 'program', path: 'programma', name: 'Your Journey' },
  accommodations: { key: 'accommodations', path: 'accommodaties', name: 'Accommodations' },
  excursions: { key: 'excursions', path: 'activiteiten', name: 'Activities' },
  flights: { key: 'flights', path: 'vluchten', name: 'Flights' },
  overview: { key: 'overview', path: 'overzicht', name: 'Overview' },
};

export const checkoutPages = {
  travelParty: { key: 'travelParty', path: 'reisgezelschap', name: 'Travel company' },
  payment: { key: 'payment', path: 'betaling', name: 'Payment' },
};

export const otherPages = {
  paymentConfirmed: { key: 'paymentConfirmed', path: 'betaling-bevestigd', name: 'Payment confirmed' },
};

export const pages = {
  ...itineraryPages,
  ...checkoutPages,
  ...otherPages,
};

export type NavigationPageKey = keyof typeof pages;
export type NavigationItem = { key: NavigationPageKey; path: string; name: string };

export function useItineraryNavigation() {
  const navigate = useNavigate();
  const itineraryNavigationItems: NavigationItem[] = useMemo(
    // typecasting necessary because Object.entries doesn't give the key literal type
    () => Object.entries(itineraryPages).map(([key, page]) => ({ ...page, key: key as NavigationPageKey })),
    [],
  );

  const checkoutNavigationItems: NavigationItem[] = useMemo(
    // typecasting necessary because Object.entries doesn't give the key literal type
    () => Object.entries(checkoutPages).map(([key, page]) => ({ ...page, key: key as NavigationPageKey })),
    [],
  );

  const navigationItems = useMemo(
    () => [...itineraryNavigationItems, ...checkoutNavigationItems],
    [checkoutNavigationItems, itineraryNavigationItems],
  );

  const currentPageMatch = useMatch(':step?');

  const currentPage = useMemo(() => {
    if (!currentPageMatch?.params?.step) {
      return navigationItems[0];
    }
    return navigationItems.find((item) => item.path === currentPageMatch?.params?.step);
  }, [currentPageMatch, navigationItems]);

  const isFirstStep = useMemo(() => currentPage === navigationItems[0], [currentPage, navigationItems]);
  const isLastStep = useMemo(
    () => currentPage === navigationItems[navigationItems.length - 1],
    [currentPage, navigationItems],
  );

  const next = useCallback(() => {
    if (!currentPage || isLastStep) return;
    const currentIndex = navigationItems.findIndex((item) => item.path === currentPage.path);
    navigate(navigationItems[currentIndex + 1].path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, isLastStep, navigationItems, navigate]);

  const back = useCallback(() => {
    if (!currentPage || isFirstStep) return;
    const currentIndex = navigationItems.findIndex((item) => item.path === currentPage.path);
    navigate(navigationItems[currentIndex - 1].path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentPage, isFirstStep, navigationItems, navigate]);

  const to = useCallback(
    (name: NavigationPageKey) => {
      const path = navigationItems.find((item) => item.key === name)?.path || '';
      const basePath = '';
      navigate(`${basePath}/${path}`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate, navigationItems],
  );

  return {
    itineraryNavigationItems,
    checkoutNavigationItems,
    navigationItems,
    currentPage,
    isFirstStep,
    isLastStep,
    next,
    back,
    to,
  };
}
